/* eslint-disable no-underscore-dangle */
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useImperativeHandle,
    useState,
    Suspense,
    ReactElement,
    useLayoutEffect,
} from 'react';
import PropTypes from 'prop-types';
import mergeRefs from 'merge-refs';
import invariant from 'tiny-invariant';
import warning from 'tiny-warning';
import * as pdfjs from 'pdfjs-dist';

import {
    cancelRunningTask,
    getDevicePixelRatio,
    isCancelException,
    makePageCallback,
} from './shared/utils';

import { isRef } from './shared/propTypes';

import type { RenderParameters } from 'pdfjs-dist/types/src/display/api';
import PageContext from './PageContext';
import { Canvas } from '@react-three/fiber';
import * as THREE from 'three';
import { Vector3, WebGLRenderer } from 'three';
import { Selection } from './helpers/selectionBox';
import Shapes, { InferShape } from './Textures/Shapes';
import useOnScreen from './helpers/useInViewport';
import Block, { inferBlock } from './Textures/Block';

import {
    useWindowSize,
    useTimeoutFn,
    useEvent,
    useDebounce,
    useUpdateEffect,
} from 'react-use';
import CanvasContext from './CanvasContext';
import { Box, CircularProgress } from '@mui/material';

const ANNOTATION_MODE = pdfjs.AnnotationMode;
export interface Vertices {
    endPoint: Vector3;
    startPoint: Vector3;
    isActive?: boolean;
    isCorrected?: boolean;
    isShown: boolean;
    percentageMatch?: string;
    color?: string;
    indices: number;
}

export type Redacts = Omit<Vertices, 'indices' | 'isShown'>;
interface WebGLVirtualRenderingContext extends WebGLRenderingContext {
    dispose: () => void;
    _canvas: any;
    composite: (gl: any) => void;
    _compositor: any;
}
export interface PageViewportProps {
    height: number;
    scale: number;
    width: number;
}
export interface inferRedact {
    getRedactData: () => Redacts[];
    setRedactData: (data: Redacts[] | null) => void;
    setRedactDataByIndex: (data: Redacts | null, index: number) => void;
    getRenderViewport: () => PageViewportProps;
    getPageScale: () => number;
    getTotalDrawnVertices: () => number;
    removeDrawnVertices: () => void;
    getViewport: () => PageViewportProps;
    scrollToSpecificRedact: (num: number) => void;
    scrollToSpecificVertices: (num: number) => void;
    resetRenderer: () => void;
    reinitRenderer: () => void;
    disposeRenderer: () => void;
}

type PageCanvasProps = {
    canvasRef?: React.Ref<HTMLCanvasElement>;
    canvasInnerRef?: React.Ref<HTMLCanvasElement>;
    parentStep?: number;
    canDrawn?: boolean;
    verticesData?: Vertices[];
    onDrawn?: (startpoint: THREE.Vector3, endpoint: THREE.Vector3) => void;
    onErased?: (index: number) => void;
    onRemove?: (index: number) => void;
    onDocumentRefresh?: () => void;
    isScrolling: boolean;
    ref?: React.Ref<inferRedact>;
};

const SuspenseTrigger = (): ReactElement => {
    throw new Promise(() => {});
};
const PageCanvas: React.FC<PageCanvasProps> = React.forwardRef((props, ref) => {
    const pageContext = React.useContext(PageContext);

    invariant(pageContext, 'Unable to find Page context.');

    const mergedProps = { ...pageContext, ...props };
    const {
        _className,
        canvasBackground,
        devicePixelRatio: devicePixelRatioProps,
        onRenderError: onRenderErrorProps,
        onRenderSuccess: onRenderSuccessProps,
        page,
        renderForms,
        rotate,
        scale,
        isScrolling,
    } = mergedProps;
    const {
        canvasRef,
        canvasInnerRef,
        canDrawn,
        onDrawn,
        onErased,
        onRemove,
        verticesData,
        parentStep,
    } = props;
    const [isDispose, setDispose] = React.useState<boolean>();
    const [isLoadedTexture, setLoadedTexture] = useState<boolean>(false);
    const canvasElement = useRef<HTMLCanvasElement>(null);
    const blockRef = useRef<THREE.MeshStandardMaterial[]>([]);
    const shapeRef = useRef<THREE.MeshStandardMaterial[]>([]);
    const innerBlockRef = useRef<Array<inferBlock>>([]);
    const innerShapeRef = useRef<Array<InferShape>>([]);
    const edgeShapeRef = useRef<Array<THREE.Mesh>>([]);
    const edgeBlockRef = useRef<Array<THREE.Mesh>>([]);
    const canvasWebGLElement = useRef<HTMLCanvasElement>(null);
    const [enableCanvas, setEnableCanvas] = useState<boolean>(false);
    const [enableCanvasMesh, setEnableCanvasMesh] = useState<boolean>(false);
    const [isRenderSuccesful, setRenderSucessful] = useState<boolean>(false);
    const [bitmapWrapper, setBitmapWrapper] = useState<null | ImageBitmap>(
        null
    );
    const [arrayRedact, setRedacts] = useState<Redacts[]>([]);
    const [
        isDraggingRedacts,
        //    setDraggingRedacts
    ] = useState<boolean>(false);
    const { width, height } = useWindowSize();

    const containerViewRef = useRef<HTMLDivElement>(null);
    const InViewport = useOnScreen(canvasElement);
    const [isInViewport, setInViewport] = useState<boolean>(false);
    const [isWASM, setWASM] = useState<boolean>(true);
    const [isCanvasWASM, setCanvasWASM] = useState<boolean>(false);

    invariant(
        page,
        'Attempted to render page canvas, but no page was specified.'
    );

    const devicePixelRatio = devicePixelRatioProps || getDevicePixelRatio();
    useUpdateEffect(() => {
        const { current: canvas } = canvasElement;
        if (typeof parentStep !== 'undefined') {
            if (canvas) {
                if (bitmapWrapper) {
                    bitmapWrapper.close();
                }
            }
        }
    }, [parentStep]);
    useLayoutEffect(() => {
        if (typeof parentStep !== 'undefined') {
            if (isInViewport === false) {
                const { current: canvas } = canvasElement;
                if (canvas) {
                    if (bitmapWrapper) {
                        bitmapWrapper.close();
                    }
                }
            }
        }
    }, [isInViewport, isDispose]);
    /**
     * Called when a page is rendered successfully.
     */
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, react-hooks/exhaustive-deps
    function onRenderSuccess() {
        if (!page) {
            // Impossible, but TypeScript doesn't know that
            return;
        }
        // eslint-disable-next-line no-underscore-dangle

        if (onRenderSuccessProps) {
            onRenderSuccessProps(makePageCallback(page, scale));
        }
    }

    /**
     * Called when a page fails to render.
     */
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function onRenderError(error: Error) {
        if (isCancelException(error)) {
            return;
        }

        warning(false, error.toString());

        if (onRenderErrorProps) {
            onRenderErrorProps(error);
        }
    }

    const renderViewport = useMemo(
        () =>
            page.getViewport({
                scale: scale * devicePixelRatio,
                rotation: rotate,
            }),
        [devicePixelRatio, page, rotate, scale]
    );

    const viewport = useMemo(
        () =>
            page.getViewport({
                scale,
                rotation: rotate,
            }),
        [page, rotate, scale]
    );

    const handleWASMFetch = useCallback(() => {
        setWASM(true);
    }, []);
    useEffect(() => {
        // eslint-disable-next-line no-underscore-dangle
        if (page && page._transport) {
            // eslint-disable-next-line no-underscore-dangle
            setWASM(false);
            // eslint-disable-next-line no-underscore-dangle
            const transporter = page._transport;
            if (transporter.wasmFactory) {
                if (transporter.wasmFactory.baseWasm) {
                    setWASM(true);
                }
                const wasmTarget: EventTarget =
                    transporter.wasmFactory.baseTarget;

                wasmTarget.addEventListener('fetch', handleWASMFetch);
            }
        }
    }, [page]);
    useEffect(() => {
        // eslint-disable-next-line no-underscore-dangle
        if (page && page._transport) {
            // eslint-disable-next-line no-underscore-dangle

            // eslint-disable-next-line no-underscore-dangle
            const transporter = page._transport;
            if (transporter.wasmFactory && transporter.wasmFactory.baseWasm) {
                setInViewport(true);
                setCanvasWASM(true);
            } else {
                setInViewport(InViewport);
                setCanvasWASM(false);
            }
        } else {
            setInViewport(InViewport);
            setCanvasWASM(false);
        }
    }, [InViewport, isWASM, isRenderSuccesful, page]);
    const [isREtexture, setReDrawnTexture] = useState<boolean>(false);

    const childContext = useMemo(
        () =>
            // Technically there cannot be page without pageIndex, pageNumber, rotate and scale, but TypeScript doesn't know that
            enableCanvas
                ? {
                      renderViewport,
                      viewport,
                  }
                : null,
        [enableCanvas, renderViewport, viewport]
    );

    useImperativeHandle(
        ref,
        () => ({
            disposeRenderer: () => {},
            resetRenderer: () => {
                setEnableCanvas(false);
                setEnableCanvasMesh(false);
            },
            reinitRenderer: () => {},
            /**
             * Get current active drawn vertices data
             * @return {Redacts[]} array of redact object
             */
            getRedactData: () => arrayRedact,
            /**
             * Set current active drawn vertices data
             */
            setRedactData: (data: Redacts[] | null) => {
                if (data) {
                    setRedacts(arrayRedact.concat(data));
                } else {
                    setRedacts([]);
                }
                setReDrawnTexture(true);
            },
            /**
             * Set current active drawn vertices data by index
             */
            setRedactDataByIndex: (data: Redacts | null, index: number) => {
                if (data) {
                    setRedacts(
                        Array.from(arrayRedact).map((o, i) => {
                            if (i === index) {
                                return data;
                            }
                            return o;
                        })
                    );
                } else {
                    setRedacts(arrayRedact.filter((o, i) => i !== index));
                }
                setReDrawnTexture(true);
            },
            removeDrawnVertices: () => {
                setRedacts([]);
            },
            /**
             * Get total drawn count
             * @return {number} length of the stored vertices in array
             */
            getTotalDrawnVertices: () => arrayRedact.length,
            /**
             * Get renderer viewport
             * @return {PageViewport} page viewport with association to display pixel density
             */
            getRenderViewport: () => renderViewport,
            /**
             * Get renderer viewport
             * @return {PageViewport} page viewport with association to window size
             */
            getPageScale: () => scale,
            getViewport: () => viewport,
            scrollToSpecificRedact: (num: number) => {
                if (arrayRedact?.some((e, i) => i === num)) {
                    const { current: arrayInnerShape } = innerShapeRef;
                    if (
                        arrayInnerShape &&
                        arrayInnerShape.some((o, i) => i === num)
                    ) {
                        const shape = arrayInnerShape.at(num);
                        shape?.scrollToView();
                    }
                }
            },
            scrollToSpecificVertices: (num: number) => {
                if (verticesData?.some((e, i) => i === num)) {
                    const { current: arrayInnerBlock } = innerBlockRef;
                    if (
                        arrayInnerBlock &&
                        arrayInnerBlock.some((o, i) => i === num)
                    ) {
                        const block = arrayInnerBlock.at(num);
                        block?.scrollToView();
                    }
                }
            },
        }),
        [arrayRedact, renderViewport, scale, verticesData, viewport]
    );
    const [isReadyBounceRenderer, cancelBounceRenderer] = useDebounce(
        () => {
            if (typeof parentStep !== 'undefined') {
                const { current: canvas } = canvasElement;
                if (!canvas || isInViewport === false || isScrolling === true) {
                    return;
                }
                const canvasRendererContext =
                    canvas.getContext('bitmaprenderer');
                if (
                    bitmapWrapper &&
                    bitmapWrapper.height !== 0 &&
                    bitmapWrapper.width !== 0
                ) {
                    canvasRendererContext?.transferFromImageBitmap(
                        bitmapWrapper
                    );
                }
            }
        },
        150,
        [isInViewport, isScrolling, bitmapWrapper]
    );
    const drawPageOnCanvas = React.useCallback(() => {
        // eslint-disable-next-line no-underscore-dangle
        if (!page || page._transport.destroyed) {
            return;
        }

        // Ensures the canvas will be re-rendered from scratch. Otherwise all form data will stay.
        page.cleanup();

        const { current: canvas } = canvasElement;

        if (!canvas) {
            return;
        }

        setEnableCanvasMesh(false);
        canvas.width = renderViewport.width;
        canvas.height = renderViewport.height;

        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.style.visibility = 'hidden';
        const canvasWorkerContext = canvas.getContext('2d', {
            alpha: true,
        }) as CanvasRenderingContext2D;

        const renderContext: RenderParameters = {
            annotationMode: renderForms
                ? ANNOTATION_MODE.ENABLE_FORMS
                : ANNOTATION_MODE.ENABLE,
            canvasContext: canvasWorkerContext,
            viewport: renderViewport,
        };
        if (canvasBackground) {
            renderContext.background = canvasBackground;
        }

        const cancellable = page.render(renderContext);
        const runningTask = cancellable;

        cancellable.promise
            .then(() => {
                canvas.style.visibility = '';
                setEnableCanvasMesh(true);
                onRenderSuccess();
            })
            .catch(onRenderError);

        // eslint-disable-next-line consistent-return
        return () => cancelRunningTask(runningTask);
    }, [
        canvasElement,
        canvasBackground,
        onRenderError,
        onRenderSuccess,
        page,
        renderForms,
        renderViewport,
        viewport.height,
        viewport.width,
    ]);
    const drawPageOnOffscreenCanvas = React.useCallback(() => {
        // eslint-disable-next-line no-underscore-dangle
        if (!page || page._transport.destroyed) {
            return;
        }

        // Ensures the canvas will be re-rendered from scratch. Otherwise all form data will stay.
        page.cleanup();

        const { current: canvas } = canvasElement;

        if (!canvas) {
            return;
        }
        if (page._transport.wasmFactory.baseWasm) {
            if (typeof drawPageOnCanvas === 'function') drawPageOnCanvas();
            return;
        }
        cancelBounceRenderer();

        setEnableCanvasMesh(false);
        canvas.width = renderViewport.width;
        canvas.height = renderViewport.height;

        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.style.visibility = 'hidden';

        const offscreen = new OffscreenCanvas(canvas.width, canvas.height);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const canvasWorkerContext = offscreen.getContext('2d', {
            alpha: true,
        }) as unknown as CanvasRenderingContext2D;

        const renderContext: RenderParameters = {
            annotationMode: renderForms
                ? ANNOTATION_MODE.ENABLE_FORMS
                : ANNOTATION_MODE.ENABLE,
            canvasContext: canvasWorkerContext,
            viewport: renderViewport,
        };
        if (canvasBackground) {
            renderContext.background = canvasBackground;
        }

        const cancellable = page.render(renderContext);
        const runningTask = cancellable;

        cancellable.promise
            .then(() => {
                // eslint-disable-next-line no-underscore-dangle

                const bitmap = offscreen.transferToImageBitmap();

                setBitmapWrapper(bitmap);

                canvas.style.visibility = '';
                setEnableCanvasMesh(true);
                onRenderSuccess();
            })
            .catch((error) => {
                onRenderError(error);

                setBitmapWrapper(null);
            });

        // eslint-disable-next-line consistent-return
        return () => cancelRunningTask(runningTask);
    }, [
        page,
        cancelBounceRenderer,
        renderViewport,
        viewport.width,
        viewport.height,
        renderForms,
        canvasBackground,
        drawPageOnCanvas,
        onRenderSuccess,
        onRenderError,
    ]);

    const drawCanvasOverlaySize = React.useCallback(() => {
        if (!page) {
            return;
        }

        const { current: canvas } = canvasElement;
        const { current: overlayCanvas } = canvasWebGLElement;
        if (!canvas || !overlayCanvas) {
            return;
        }

        overlayCanvas.width = renderViewport.width;
        overlayCanvas.height = renderViewport.height;

        overlayCanvas.style.width = `${Math.floor(viewport.width)}px`;
        overlayCanvas.style.height = `${Math.floor(viewport.height)}px`;
    }, [canvasElement, page, renderViewport, viewport.height, viewport.width]);

    const drawRectOnCanvas = React.useCallback(() => {
        if (!page) {
            return;
        }

        // Ensures the canvas will be re-rendered from scratch. Otherwise all form data will stay.
        page.cleanup();

        const { current: canvas } = canvasElement;

        if (!canvas) {
            return;
        }

        canvas.width = renderViewport.width;
        canvas.height = renderViewport.height;

        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.style.visibility = 'hidden';
    }, [canvasElement, page, renderViewport, viewport.height, viewport.width]);
    const [px_ratio, setPxRatio] = useState<number>(0);
    const [isZoom, setZoom] = useState<boolean>(false);

    const isZooming = (): boolean => {
        const newPx_ratio =
            window.devicePixelRatio ||
            window.screen.availWidth / document.documentElement.clientWidth;
        if (newPx_ratio !== px_ratio) {
            setPxRatio(newPx_ratio);

            return true;
        }
        return false;
    };
    useEvent('resize', () => {
        if (isZooming()) {
            setZoom(true);
        }
    });
    const [isReady, canceled] = useDebounce(
        () => {
            if (isZoom) {
                if (isInViewport) {
                    if (typeof parentStep !== 'undefined') {
                        if (isReadyBounceRenderer() === false) {
                            cancelBounceRenderer();
                        }
                        drawPageOnOffscreenCanvas();
                    } else {
                        drawPageOnCanvas();
                    }
                } else {
                    drawRectOnCanvas();
                }
                setRenderSucessful(true);
                setZoom(false);
            }
        },
        1000,
        [isZoom]
    );
    useEffect(() => {
        canceled();
        setPxRatio(
            window.devicePixelRatio ||
                window.screen.availWidth / document.documentElement.clientWidth
        );
    }, []);

    useEffect(
        () => {
            if (isZoom === false && isReady() !== false) {
                if (isInViewport) {
                    if (typeof parentStep !== 'undefined') {
                        drawPageOnOffscreenCanvas();
                    } else {
                        drawPageOnCanvas();
                    }
                } else {
                    drawRectOnCanvas();
                }
                setRenderSucessful(true);
            }
        },
        // Ommitted callbacks so they are not called every time they change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            canvasBackground,
            canvasElement,
            devicePixelRatio,
            page,
            renderForms,
            renderViewport,
            viewport,
        ]
    );

    useLayoutEffect(() => {
        if (isZoom === false) {
            if (isInViewport) {
                if (typeof parentStep !== 'undefined') {
                    drawPageOnOffscreenCanvas();
                } else {
                    drawPageOnCanvas();
                }
            } else {
                drawRectOnCanvas();
            }
            setRenderSucessful(true);
        }
    }, [isInViewport, isDispose]);

    const cleanup = useCallback(() => {
        const { current: canvas } = canvasElement;

        /**
         * Zeroing the width and height cause most browsers to release graphics
         * resources immediately, which can greatly reduce memory consumption.
         */
        if (canvas) {
            canvas.width = 0;
            canvas.height = 0;
        }
    }, [canvasElement]);

    useEffect(() => cleanup, [cleanup]);

    useEffect(() => {
        const { current: canvasOverlay } = canvasWebGLElement;

        if (!canvasOverlay || !isLoadedTexture) {
            return;
        }

        canvasOverlay.width = renderViewport.width;
        canvasOverlay.height = renderViewport.height;
        canvasOverlay.style.width = `${Math.floor(viewport.width)}px`;
        canvasOverlay.style.height = `${Math.floor(viewport.height)}px`;
    }, [
        isLoadedTexture,
        renderViewport.height,
        renderViewport.width,
        viewport.height,
        viewport.width,
    ]);
    /**
     * Handler for selection box when the selectiion is triggered by pointer/mouse listener
     * @param {any} objs - objects from selection
     * @param {THREE.Vector3} startPoint - start point of selected bounding box
     * @param {THREE.Vector3} endPoint - end point of selected bounding box
     */
    const handleSeletionBoxChanged = React.useCallback(
        (objs: any, startPoint: THREE.Vector3, endPoint: THREE.Vector3) => {
            if (
                endPoint &&
                startPoint &&
                (endPoint.x !== startPoint.x || endPoint.y !== startPoint.y) &&
                Math.abs(endPoint.x - startPoint.x) <= 0.012 === false &&
                Math.abs(endPoint.y - startPoint.y) <= 0.012 === false
            ) {
                setRedacts((prevState: Redacts[]) => [
                    ...prevState,
                    {
                        endPoint: new Vector3().set(
                            endPoint.x,
                            endPoint.y,
                            endPoint.z
                        ),
                        startPoint: new Vector3().set(
                            startPoint.x,
                            startPoint.y,
                            startPoint.z
                        ),
                    },
                ]);
                if (typeof onDrawn === 'function') {
                    onDrawn(startPoint, endPoint);
                    // setAlreadyDrawn(true);
                }
            }
        },
        [onDrawn]
    );
    /**
     * Handler for erasing drawn box
     * @param {THREE.Vector3} startPoint - start point of selected bounding box
     * @param {THREE.Vector3} endPoint - end point of selected bounding box
     * @param {number} index - ordering number based in array map
     */
    const handleEraseSelectedBox = React.useCallback(
        (startPoint: THREE.Vector3, endPoint: THREE.Vector3, index: number) => {
            if (typeof onErased === 'function') onErased(index);
            setReDrawnTexture(true);
        },
        [onErased]
    );
    /**
     * Handler for erasing saved box
     * @param {THREE.Vector3} startPoint - start point of selected bounding box
     * @param {THREE.Vector3} endPoint - end point of selected bounding box
     * @param {number} index - ordering number based in array map
     */
    const handleEraseSavedVertices = React.useCallback(
        (
            startPoint: THREE.Vector3,
            endPoint: THREE.Vector3,
            index: number,
            indices: number
        ) => {
            const { current: arrayInnerBlock } = innerBlockRef;
            if (
                arrayInnerBlock &&
                arrayInnerBlock.some((o, i) => i === index)
            ) {
                if (typeof onRemove === 'function') onRemove(indices);
                setReDrawnTexture(true);
            }
        },
        [onRemove, viewport.scale]
    );

    React.useEffect(() => {
        setLoadedTexture(isInViewport);
    }, [isInViewport, isScrolling, isDispose]);

    const groupRef = useRef<THREE.Group | null>(null);
    React.useLayoutEffect(() => {
        const { current: arrayShapes } = shapeRef;
        const { current: arrayBlock } = blockRef;
        const { current: arrayEdgeShapes } = edgeShapeRef;
        const { current: arrayEdgeBlock } = edgeBlockRef;

        arrayShapes.forEach((el) => {
            const texture = el;
            texture.needsUpdate = true;
        });
        arrayBlock.forEach((el) => {
            const texture = el;
            texture.needsUpdate = true;
        });
        arrayEdgeShapes.forEach((el) => {
            const texture = el;
            texture.updateMatrix();
            texture.updateMatrixWorld();
            texture.matrixWorldNeedsUpdate = true;
            texture.updateMorphTargets();
            texture.updateWorldMatrix(true, true);
            // texture.updateMatrix()
        });
        arrayEdgeBlock.forEach((el) => {
            const texture = el;
            texture.updateMatrix();
            texture.updateMatrixWorld();
            texture.matrixWorldNeedsUpdate = true;
            texture.updateMorphTargets();
            texture.updateWorldMatrix(true, true);
            // texture.updateMatrix()
        });
        if (isREtexture) {
            setReDrawnTexture(false);
        }
    }, [isREtexture, width, height]);

    const getBlockVerticesColor = React.useCallback(
        (
            active: boolean | undefined,
            corrected: boolean | undefined,
            percentage: string | undefined,
            color: string | undefined
        ) => {
            if (typeof active === 'boolean' && active) {
                return 'purple';
            }
            if (typeof color === 'string') {
                return color;
            }
            if (typeof percentage === 'string') {
                const percentageInteger = parseInt(percentage, 10);
                if (percentageInteger >= 100) {
                    return 'green';
                }
                if (percentageInteger > 50) {
                    return 'orange';
                }
                if (percentageInteger > 0 && percentageInteger <= 50) {
                    return 'brown';
                }
                return 'red';
            }
            return 'red';
        },
        []
    );

    React.useEffect(() => {
        if (isDispose) {
            setDispose(false);
        }
    }, [isDispose]);
    // eslint-disable-next-line no-underscore-dangle

    React.useLayoutEffect(() => {
        if (isRenderSuccesful && isInViewport) {
            setEnableCanvas(true);
        } else {
            setEnableCanvasMesh(false);
            setEnableCanvas(false);
        }
    }, [isRenderSuccesful, isInViewport, isScrolling]);
    const handleEnabledCanvasMesh = React.useCallback(() => {
        setReDrawnTexture(true);
    }, []);
    const [, cancel, reset] = useTimeoutFn(handleEnabledCanvasMesh, 800);
    React.useEffect(() => {
        cancel();
    }, []);
    React.useLayoutEffect(() => {
        if (enableCanvasMesh) {
            drawCanvasOverlaySize();
            reset();
        }
    }, [enableCanvasMesh]);
    React.useEffect(() => {
        if (enableCanvas) {
            const { current: canvasHTML } = canvasWebGLElement;
            if (canvasHTML) {
                const context: WebGLVirtualRenderingContext =
                    canvasHTML.getContext(
                        'webgl'
                    ) as WebGLVirtualRenderingContext;
                let version = null;
                if (context) {
                    version = context.getParameter(context.VERSION);

                    if (version === null) {
                        // context.dispose();
                        setDispose(true);
                        setEnableCanvasMesh(false);

                        // eslint-disable-next-line global-require
                    }
                }
            }
        }
    }, [enableCanvas]);

    const handleCanvasRenderer = React.useCallback(
        (canvas: any) =>
            new WebGLRenderer({
                canvas,

                alpha: true,
                preserveDrawingBuffer: true,
                precision: 'lowp',
            }),
        []
    );

    return (
        <CanvasContext.Provider value={childContext}>
            <div className="overlay-pdf__Canvas">
                <div ref={containerViewRef} className="twoDimension__Canvas">
                    <canvas
                        className={`${_className}__canvas`}
                        dir="ltr"
                        ref={mergeRefs(canvasInnerRef, canvasElement)}
                    />
                </div>
                {enableCanvas && !isDispose && (
                    <div className="threeDimension__Canvas">
                        <Suspense
                            fallback={
                                isCanvasWASM ? (
                                    <Box
                                        position="relative"
                                        width={viewport.width}
                                        height={viewport.height}
                                    >
                                        <Box className="align-loader">
                                            <CircularProgress
                                                className="mr-2 margin-loader"
                                                size={54}
                                                color="inherit"
                                                title="progress loading"
                                            />
                                        </Box>
                                    </Box>
                                ) : (
                                    <canvas
                                        dir="ltr"
                                        ref={mergeRefs(
                                            canvasRef,
                                            canvasWebGLElement
                                        )}
                                    />
                                )
                            }
                        >
                            {enableCanvasMesh ? (
                                <Canvas
                                    performance={{
                                        current: 1,
                                        min: 0.1,
                                        max: 1,
                                        debounce: 800,
                                    }}
                                    frameloop={
                                        isLoadedTexture ? 'demand' : 'never'
                                    }
                                    gl={handleCanvasRenderer}
                                    camera={{ position: [0, 0, -1] }}
                                    className={`${_className}__canvas_overlay overlay`}
                                    dir="ltr"
                                    ref={mergeRefs(
                                        canvasRef,
                                        canvasWebGLElement
                                    )}
                                >
                                    <>
                                        <ambientLight intensity={7} />
                                        <group>
                                            {verticesData?.map(
                                                (element, index) => (
                                                    <Block
                                                        shown={element.isShown}
                                                        color={getBlockVerticesColor(
                                                            element.isActive,
                                                            element.isCorrected,
                                                            element.percentageMatch,
                                                            element.color
                                                        )}
                                                        canRemove={canDrawn}
                                                        edgeRef={(ref) => {
                                                            if (ref !== null)
                                                                edgeBlockRef.current[
                                                                    index
                                                                ] = ref;
                                                        }}
                                                        textureRef={(ref) => {
                                                            if (ref !== null)
                                                                blockRef.current[
                                                                    index
                                                                ] = ref;
                                                        }}
                                                        ref={(ref) => {
                                                            if (ref !== null)
                                                                innerBlockRef.current[
                                                                    index
                                                                ] = ref;
                                                        }}
                                                        key={index}
                                                        startPoint={
                                                            element.startPoint
                                                        }
                                                        endPoint={
                                                            element.endPoint
                                                        }
                                                        onRemove={() =>
                                                            handleEraseSavedVertices(
                                                                element.startPoint,
                                                                element.endPoint,
                                                                index,
                                                                element.indices
                                                            )
                                                        }
                                                    />
                                                )
                                            )}
                                        </group>
                                        <group
                                            ref={groupRef}
                                            matrixAutoUpdate={false}
                                            matrixWorldAutoUpdate={false}
                                        >
                                            {arrayRedact.map(
                                                (element, index) => (
                                                    <Shapes
                                                        edgeRef={(ref) => {
                                                            if (ref !== null)
                                                                edgeShapeRef.current[
                                                                    index
                                                                ] = ref;
                                                        }}
                                                        textureRef={(ref) => {
                                                            if (ref !== null)
                                                                shapeRef.current[
                                                                    index
                                                                ] = ref;
                                                        }}
                                                        ref={(ref) => {
                                                            if (ref !== null)
                                                                innerShapeRef.current[
                                                                    index
                                                                ] = ref;
                                                        }}
                                                        key={index}
                                                        startPoint={
                                                            element.startPoint
                                                        }
                                                        endPoint={
                                                            element.endPoint
                                                        }
                                                        onErase={() =>
                                                            handleEraseSelectedBox(
                                                                element.startPoint,
                                                                element.endPoint,
                                                                index
                                                            )
                                                        }
                                                        // onDragging={handleDraggingFromShapes}
                                                    />
                                                )
                                            )}
                                        </group>
                                        <group>
                                            {!isDraggingRedacts && canDrawn && (
                                                <Selection
                                                    onSelectionChanged={
                                                        handleSeletionBoxChanged
                                                    }
                                                />
                                            )}
                                        </group>
                                    </>
                                </Canvas>
                            ) : (
                                <SuspenseTrigger />
                            )}
                        </Suspense>
                    </div>
                )}
            </div>
        </CanvasContext.Provider>
    );
});

PageCanvas.propTypes = {
    canvasRef: isRef,
    canDrawn: PropTypes.bool,
    onDrawn: PropTypes.func,
    onRemove: PropTypes.func,
    // eslint-disable-next-line react/forbid-prop-types
    verticesData: PropTypes.any,
};

export default PageCanvas;
